import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import CardList from "../ui/CardList.jsx";
import FullScreenPanel from "../ui/FullScreenPanel.jsx";
import PhotoButton from "../ui/PhotoButton.jsx";
import { shrinkPhoto } from "../ui/shrinkPhoto";
import { phoneUi } from "../ui/phoneUi";
import { PartJobCard } from "./ProgressScreen.jsx";
import {
  isArchivedRevision,
  loadPart,
  loadPartDocuments,
  loadPartRevisions,
  partImageProblem,
  partPhotoName,
  partProcessSteps,
  setPartFlag,
  uploadPartImage,
} from "../../features/parts/partData";
import { loadPartProgress, searchParts } from "../../features/progress/progressData";
import { loadCustomers } from "../../features/customers/customerData";
import { currentActor } from "../../utils/beacons";
import { parseApiError } from "../../utils/apiError";
import { stamp } from "../../utils/tmDefects";
import "../mobile.css";

// The phone Parts page (plan Phase 2): search (SKU or description); one part (?id=N or the desktop's
// /parts/:partId link) with its image, drawing and files, revision, customer, material, process steps,
// cost summary and open jobs; flag / unflag and take a part photo (the desktop's own actions). Create /
// edit, revisions and approvals, PPAP, cost editing, DXF and bulk entry stay on the desktop.
// Layout only: features/parts/partData.js is the desktop page's own code.

const money = (n) => (n == null || Number.isNaN(Number(n)) ? "—" : `$${Number(n).toFixed(2)}`);

function partIdOf(searchParams, routeId) {
  const raw = searchParams.get("id") || routeId;
  if (!raw || !/^\d+$/.test(raw)) return null;
  const n = Number(raw);
  return n > 0 ? n : null;
}

export default function PartsScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { partId: routeId } = useParams();
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [searched, setSearched] = useState(null); // the text last searched (null = none yet)
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const fromList = useRef(false);
  const id = partIdOf(searchParams, routeId);
  const toList = () => {
    if (fromList.current) { fromList.current = false; navigate(-1); } else if (routeId) navigate("/parts", { replace: true }); else setSearchParams({}, { replace: true });
  };
  const seq = useRef(0);
  const search = async (q) => {
    const text = String(q || "").trim();
    const mine = ++seq.current;
    if (!text) { setRows([]); setSearched(null); setBusy(false); return; }
    setBusy(true);
    const found = await searchParts(text);
    if (mine !== seq.current) return;
    setRows(found);
    setSearched(text);
    setBusy(false);
  };

  if (id) return <PartDetail key={id} partId={id} onAll={toList} />;
  return (
    <div className="jq-screen" data-testid="phone-parts">
      <CardList
        items={rows}
        getKey={(p) => p.part_id}
        onItemClick={(p) => { fromList.current = true; setSearchParams({ id: String(p.part_id) }); }}
        renderCard={(p) => (
          <div data-testid={`part-card-${p.part_id}`} className="d-flex gap-2 align-items-center">
            {p.image_path && <img src={p.image_path} alt="" className="jq-thumb" style={{ width: 48, height: 48, objectFit: "contain" }} />}
            <div>
              <div className="jq-qcard-title mb-0">{p.product_id}</div>
              <div className="jq-qcard-meta">{p.description}</div>
            </div>
          </div>
        )}
        loading={busy}
        empty={searched != null ? `No parts match "${searched}".` : "Search for a part by SKU or description."}
        testId="part-list"
        search={{ value: query, onChange: setQuery, onSubmit: search, placeholder: "SKU or description" }}
      />
      {query.trim() && query.trim() !== searched && <button type="button" className="btn btn-primary w-100" onClick={() => search(query)}>Search</button>}
    </div>
  );
}

function PartDetail({ partId, onAll }) {
  const [part, setPart] = useState(null);
  const [error, setError] = useState(null);
  const [docs, setDocs] = useState(undefined);
  const [revisions, setRevisions] = useState(null);
  const [jobs, setJobs] = useState(undefined); // undefined = loading; [] = none; null = unreadable
  const [customerName, setCustomerName] = useState(null);
  const [flagBusy, setFlagBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState(null);
  const [enlarged, setEnlarged] = useState(false);

  const rereadPart = useCallback(async () => {
    try {
      const p = await loadPart(partId);
      setPart(p);
      return p;
    } catch (err) {
      setError(err.message || "Couldn't reach JobQ.");
      return null;
    }
  }, [partId]);

  useEffect(() => {
    let live = true;
    rereadPart().then((p) => {
      if (!live || !p) return;
      loadPartRevisions(p.product_id).then((r) => { if (live) setRevisions(r); });
      if (p.customer_id) loadCustomers().then((cs) => { if (live) setCustomerName((cs.find((c) => c.customer_id === p.customer_id) || {}).name || null); }).catch(() => {});
    });
    loadPartDocuments(partId).then((d) => { if (live) setDocs(d); });
    // GET /progress/part/{id} answers 400 when the part has no active jobs: that is "none open". Any
    // other failure (a timeout, a 500) is "couldn't read", never "none".
    loadPartProgress(partId).then((j) => { if (live) setJobs(j); }).catch((e) => { if (live) setJobs(e && e.status === 400 ? [] : null); });
    return () => { live = false; };
  }, [partId, rereadPart]);

  const back = <button type="button" className="btn btn-link px-0 mb-2" onClick={onAll}><i className="fas fa-chevron-left me-1" aria-hidden="true" />All parts</button>;
  if (error && !part) return <div className="jq-screen" data-testid="phone-part">{back}<div className="jq-note jq-note-danger" role="alert">{error}</div></div>;
  if (!part) return <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Loading…</div>;

  const archived = isArchivedRevision(part);
  const steps = partProcessSteps(part);
  const rollup = part.rollup || null;

  const toggleFlag = async () => {
    setFlagBusy(true);
    try {
      await setPartFlag({ partId: part.part_id, on: !part.flagged, readOnly: archived, ui: phoneUi, actor: currentActor() });
      // Re-read either way: a refusal ("already flagged") can mean someone else changed it meanwhile.
      await rereadPart();
    } finally {
      setFlagBusy(false);
    }
  };
  const takePhoto = async (picked) => {
    setPhotoError(null);
    // The API saves an upload on the part at once (labels and packing documents use it): no undo here.
    if (part.image_path && !(await phoneUi.confirm("Replace this part's picture with the new photo?"))) return;
    setPhotoBusy(true);
    try {
      const small = await shrinkPhoto(picked);
      const problem = await partImageProblem(small); // the desktop's 3000 x 3000 limit (shrinkPhoto can hand back the original)
      if (problem) { setPhotoError(problem); return; }
      const file = new File([small], partPhotoName(part.part_id), { type: small.type || "image/jpeg" });
      const r = await uploadPartImage({ partId: part.part_id, file, readOnly: archived, ui: phoneUi });
      if (r.error) setPhotoError(parseApiError(r.error, "Failed to upload image"));
      if (r.done) await rereadPart();
    } catch (err) {
      setPhotoError(parseApiError(err && err.message, "Failed to upload image"));
    } finally {
      setPhotoBusy(false);
    }
  };

  return (
    <div className="jq-screen" data-testid="phone-part">
      {back}
      <section className="jq-card" data-testid="part-header">
        <div className="d-flex align-items-center gap-2">
          <span className="fw-bold fs-5 flex-grow-1">{part.product_id}</span>
          <span className="jq-tag">Rev {part.revision_code || "—"}</span>
          {archived && <span className="jq-tag jq-tag-warn">Archived</span>}
          {part.active_status === false && <span className="jq-tag">Inactive</span>}
        </div>
        <div>{part.description}</div>
        {customerName && <div className="jq-muted">{customerName}</div>}
        {part.image_path ? (
          <button type="button" className="btn p-0 mt-2 w-100" onClick={() => setEnlarged(true)} aria-label="Enlarge the part image">
            <img src={part.image_path} alt="Part" style={{ width: "100%", maxHeight: 260, objectFit: "contain" }} data-testid="part-image" />
          </button>
        ) : <div className="jq-muted mt-2">No image yet.</div>}
      </section>

      {part.flagged && (
        <div className="jq-note jq-note-warn mb-2" data-testid="part-flag">
          <div className="fw-semibold">Flagged for review</div>
          {part.flag_reason}
          {(part.flagged_by_name || part.flagged_at) && (
            <div className="jq-muted">{[part.flagged_by_name, part.flagged_at ? stamp(part.flagged_at) : null].filter(Boolean).join(" · ")}</div>
          )}
        </div>
      )}
      {!archived && (
        <div className="d-flex gap-2 mb-2">
          <button type="button" className={`btn ${part.flagged ? "btn-outline-secondary" : "btn-outline-warning"} flex-grow-1`} disabled={flagBusy} onClick={toggleFlag}>
            {part.flagged ? "Clear the flag" : "Flag for review"}
          </button>
          <div className="flex-grow-1">
            <PhotoButton onPick={takePhoto} label="Take a part photo" busy={photoBusy} className="btn btn-outline-primary w-100" testId="part-photo" />
          </div>
        </div>
      )}
      {photoError && <div className="jq-note jq-note-danger mb-2" role="alert">{photoError}</div>}

      <section className="jq-card" data-testid="part-material">
        <h2 className="jq-section-title">Material</h2>
        <dl className="jq-kv mb-0">
          <div><dt>Stock</dt><dd>{part.stock_type || "—"}</dd></div>
          <div><dt>Material</dt><dd>{[part.material_type, part.display_thickness, part.surface_finish].filter(Boolean).join(" · ") || "—"}</dd></div>
          {Number(part.size_length) > 0 && <div><dt>Size</dt><dd>{part.size_length} × {Number(part.size_width) > 0 ? part.size_width : "—"} in</dd></div>}
          {Number(part.weight_lbs) > 0 && <div><dt>Weight</dt><dd>{Number(part.weight_lbs).toFixed(2)} lb</dd></div>}
        </dl>
      </section>

      <section className="jq-card" data-testid="part-steps">
        <h2 className="jq-section-title">Process steps</h2>
        {steps.length ? <ol className="mb-0 ps-3">{steps.map((s) => <li key={s}>{s}</li>)}</ol> : <div className="jq-muted">None listed.</div>}
      </section>

      <section className="jq-card" data-testid="part-cost">
        <h2 className="jq-section-title">Cost</h2>
        <dl className="jq-kv mb-0">
          <div><dt>Labor</dt><dd>{money(part.cost_labor)}</dd></div>
          <div><dt>Material</dt><dd>{money(part.material_cost_component)}</dd></div>
          <div>
            <dt>Total cost</dt>
            <dd>
              {money(rollup ? rollup.live_total : part.cost_labor_material)}
              {rollup && rollup.is_stale && <div className="text-warning small">stored {money(rollup.stored_total)}: out of date</div>}
            </dd>
          </div>
          <div><dt>Price</dt><dd>{money(part.price)}{part.price_source ? <div className="jq-muted small">{part.price_source}</div> : null}</dd></div>
        </dl>
      </section>

      <section className="jq-card" data-testid="part-files">
        <h2 className="jq-section-title">Drawing and files</h2>
        {docs === undefined && <div className="jq-muted">Loading…</div>}
        {docs === null && <div className="jq-muted">Couldn't read the files.</div>}
        {docs && docs.length === 0 && <div className="jq-muted">No files.</div>}
        {(docs || []).map((d) => (
          <a key={d.document_id || d.file_url} href={d.file_url} target="_blank" rel="noreferrer" className="d-block mb-1">
            {d.display_name || d.original_filename || "File"}
            {d.original_filename && d.original_filename.includes(".") && <span className="jq-muted"> · {d.original_filename.split(".").pop().toUpperCase()}</span>}
          </a>
        ))}
      </section>

      {revisions && revisions.length > 1 && (
        <section className="jq-card" data-testid="part-revisions">
          <h2 className="jq-section-title">Revisions</h2>
          {revisions.map((r) => (
            <div key={r.part_id} className="d-flex">
              <span className="flex-grow-1">Rev {r.revision_code}{r.is_current_revision ? " (current)" : ""}</span>
              {r.ppap_status && <span className="jq-muted">{r.ppap_status}</span>}
            </div>
          ))}
        </section>
      )}

      <h2 className="jq-section-title mt-3">Open jobs</h2>
      {jobs === undefined && <div className="jq-muted">Loading…</div>}
      {jobs === null && <div className="jq-note jq-note-danger" role="alert">Couldn't read the open jobs. Go back and open the part again.</div>}
      {jobs && jobs.length === 0 && <div className="jq-list-state">No open jobs for this part.</div>}
      {(jobs || []).map((j) => <PartJobCard key={j.job_id} job={j} />)}

      <FullScreenPanel open={enlarged} title={part.product_id} onClose={() => setEnlarged(false)} testId="part-image-panel">
        {part.image_path && <img src={part.image_path} alt="Part, enlarged" style={{ width: "100%" }} />}
      </FullScreenPanel>
    </div>
  );
}
